import { useEffect, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { FieldConfig } from "../../formConfig";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** Parse an ISO "YYYY-MM-DD" string into a local Date (no timezone shift). */
function parseISO(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function formatDisplay(v: string): string {
  const d = parseISO(v);
  if (!d) return "";
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${mo}/${day}/${d.getFullYear()}`;
}

export default function DateField({ field }: { field: FieldConfig }) {
  const { control } = useFormContext();
  const {
    field: { value, onChange, onBlur, name }
  } = useController({ name: field.id, control, defaultValue: "" });

  const strValue = (value as string) ?? "";
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => parseISO(strValue) ?? new Date());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Keep the visible month in sync when an external value change happens.
  useEffect(() => {
    const d = parseISO(strValue);
    if (d) setViewDate(d);
  }, [strValue]);

  // Close the popover when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onBlur]);

  const selected = parseISO(strValue);

  const grid = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewDate]);

  const pick = (d: Date) => {
    onChange(toISO(d));
    setOpen(false);
    onBlur();
  };

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const changeMonth = (delta: number) =>
    setViewDate((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  return (
    <div className="date-field" ref={wrapRef}>
      <div className="date-input-row">
        <input
          id={field.id}
          name={name}
          type="text"
          inputMode="numeric"
          placeholder="MM/DD/YYYY"
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore
          data-bwignore="true"
          data-dashlane-ignore="true"
          className="date-text-input"
          value={strValue ? formatDisplay(strValue) : ""}
          onChange={(e) => {
            // Allow manual typing in MM/DD/YYYY form.
            const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(e.target.value.trim());
            if (m) {
              const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
              if (!isNaN(d.getTime())) { onChange(toISO(d)); return; }
            }
            if (e.target.value.trim() === "") onChange("");
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
        />
        <button
          type="button"
          className="date-toggle"
          aria-label="Open calendar"
          onClick={() => setOpen((o) => !o)}
        >📅</button>
      </div>

      {open && (
        <div className="date-popover" role="dialog">
          <div className="date-popover-head">
            <button type="button" onClick={() => changeMonth(-1)} aria-label="Previous month">‹</button>
            <span className="date-popover-title">{MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
            <button type="button" onClick={() => changeMonth(1)} aria-label="Next month">›</button>
          </div>
          <div className="date-grid">
            {WEEKDAYS.map((w) => <span key={w} className="date-weekday">{w}</span>)}
            {grid.map((d, i) =>
              d ? (
                <button
                  key={i}
                  type="button"
                  className={"date-day" + (selected && sameDay(d, selected) ? " selected" : "")}
                  onClick={() => pick(d)}
                >{d.getDate()}</button>
              ) : (
                <span key={i} className="date-day empty" />
              )
            )}
          </div>
          <div className="date-popover-foot">
            <button type="button" className="date-today" onClick={() => pick(new Date())}>Today</button>
            {strValue && (
              <button type="button" className="date-clear" onClick={() => { onChange(""); setOpen(false); }}>Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
